import React, { useState, useEffect } from "react";
import { fetchTourDetails, fetchWellnessDetails } from "./chatbotApi";
import "./TourShowcase.css";

// Backend text often contains HTML (<p>, <ul><li>, <br>, &amp; ...).
// Turn it into clean plain text (keeps line breaks + bullets). DOMParser never runs scripts.
function toText(v) {
  if (v == null) return "";
  let s = String(v);
  if (!/[<&]/.test(s)) return s;
  s = s
    .replace(/<\s*br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|h[1-6]|ul|ol|tr)>/gi, "\n")
    .replace(/<li[^>]*>/gi, "• ")
    .replace(/<\/li>/gi, "\n");
  const doc = new DOMParser().parseFromString(s, "text/html");
  return (doc.body.textContent || "").replace(/\u00a0/g, " ").replace(/[ \t]+\n/g, "\n").replace(/\n{3,}/g, "\n\n").trim();
}

export default function TourShowcase({ preview, onClose, onOpenPage, onError }) {
  // When onOpenPage is given, links open in the SAME tab (handled by the parent)
  const openHere = (e) => {
    if (!onOpenPage) return;
    e.preventDefault();
    onOpenPage(preview.url);
  };

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImageIdx, setActiveImageIdx] = useState(0);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    let isMounted = true;
    if (!preview || !preview.id) {
      setLoading(false);
      return;
    }

    setLoading(true);

    const fetchPromise =
      preview.type === "wellness"
        ? fetchWellnessDetails(preview.id)
        : fetchTourDetails(preview.id);

    fetchPromise
      .then((res) => {
        if (!isMounted) return;
        setData(res);
        setLoading(false);
      })
      .catch((err) => {
        if (!isMounted) return;
        console.warn("Failed to load showcase data:", err);
        setLoading(false);
        onError?.();
      });

    return () => {
      isMounted = false;
    };
  }, [preview]);

  if (loading) {
    return (
      <div className="tgh-showcase-container">
        <div className="tgh-showcase-header">
          <span className="tgh-showcase-badge">🏔️ Visual Showcase</span>
          <button className="tgh-showcase-close-btn" onClick={onClose} title="Close Preview">
            ✕
          </button>
        </div>
        <div className="tgh-showcase-loader">
          <div className="tgh-showcase-spinner" />
          <span>Loading visual showcase...</span>
        </div>
      </div>
    );
  }

  // Fallback / Normalized Data
  const rawImages =
    data?.image_url && data.image_url.length > 0
      ? Array.isArray(data.image_url)
        ? data.image_url
        : [data.image_url]
      : data?.images || [];

  const images = rawImages.filter(Boolean);
  const title = toText(data?.title || preview.title || "Tour Itinerary");
  const location = toText(
    data?.location?.city
      ? `${data.location.city}, ${data.location.state || ""}`
      : data?.location || "Himalayan Region"
  );
  const duration = toText(data?.duration || "Multi-day");
  const groupSize = data?.group_size
    ? `Max ${data.group_size} guests`
    : data?.is_group_tour
    ? "Group Tour"
    : "Private / Flexible";
  const price = data?.price || data?.final_price;
  const rating = data?.avgRating || data?.rating || 4.9;
  const category = toText(
    data?.category || (preview.type === "wellness" ? "Wellness" : "Signature Tour")
  );
  const description = toText(
    data?.description ||
      (Array.isArray(data?.about) ? data.about.join("\n") : data?.about) ||
      ""
  );
  const itinerary = Array.isArray(data?.itinerary) ? data.itinerary : [];
  const inclusions = Array.isArray(data?.inclusions) ? data.inclusions : [];
  const exclusions = Array.isArray(data?.exclusions) ? data.exclusions : [];
  const highlights = Array.isArray(data?.highlights) ? data.highlights : [];

  const mainImageUrl =
    images[activeImageIdx] ||
    images[0] ||
    "https://images.unsplash.com/photo-1506744038136-46273834b3fb?w=800&q=80";

  return (
    <div className="tgh-showcase-container">
      {/* ── Top Header ── */}
      <div className="tgh-showcase-header">
        <div className="tgh-showcase-title-row">
          <span className="tgh-showcase-badge">🏔️ Visual Showcase</span>
          <span className="tgh-showcase-category">{category}</span>
        </div>
        <div className="tgh-showcase-actions">
          <a
            href={preview.url}
            {...(onOpenPage ? { onClick: openHere } : { target: "_blank", rel: "noopener noreferrer" })}
            className="tgh-showcase-open-btn"
            title="Open Full Page"
          >
            ↗ Open Page
          </a>
          <button className="tgh-showcase-close-btn" onClick={onClose} title="Close Showcase">
            ✕
          </button>
        </div>
      </div>

      {/* ── Scrollable Showcase Body ── */}
      <div className="tgh-showcase-body">
        {/* Hero Gallery */}
        <div className="tgh-showcase-gallery">
          <div className="tgh-showcase-hero-img-wrap">
            <img src={mainImageUrl} alt={title} className="tgh-showcase-hero-img" />
            <div className="tgh-showcase-hero-overlay">
              <div className="tgh-showcase-pill-row">
                <span className="tgh-showcase-pill">📍 {location}</span>
                <span className="tgh-showcase-pill">⏱️ {duration}</span>
                <span className="tgh-showcase-pill">👥 {groupSize}</span>
                {rating > 0 && <span className="tgh-showcase-pill">⭐ {rating}</span>}
              </div>
            </div>
          </div>

          {/* Thumbnails */}
          {images.length > 1 && (
            <div className="tgh-showcase-thumbs">
              {images.slice(0, 6).map((img, idx) => (
                <button
                  key={idx}
                  className={`tgh-showcase-thumb-btn ${
                    idx === activeImageIdx ? "tgh-showcase-thumb-btn--active" : ""
                  }`}
                  onClick={() => setActiveImageIdx(idx)}
                >
                  <img src={img} alt={`${title} view ${idx + 1}`} className="tgh-showcase-thumb-img" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Title & Pricing Overview */}
        <div className="tgh-showcase-section tgh-showcase-main-info">
          <h2 className="tgh-showcase-title">{title}</h2>

          {price && (
            <div className="tgh-showcase-pricing-card">
              <div>
                <span className="tgh-showcase-price-label">Live Tariff</span>
                <div className="tgh-showcase-price">
                  ₹{Number(price).toLocaleString("en-IN")}
                  <span className="tgh-showcase-price-unit"> / person</span>
                </div>
              </div>
              <a
                href={preview.url}
                {...(onOpenPage ? { onClick: openHere } : { target: "_blank", rel: "noopener noreferrer" })}
                className="tgh-showcase-book-cta"
              >
                Book Package ↗
              </a>
            </div>
          )}
        </div>

        {/* Showcase Tabs */}
        <div className="tgh-showcase-tabs">
          <button
            className={`tgh-showcase-tab ${activeTab === "overview" ? "tgh-showcase-tab--active" : ""}`}
            onClick={() => setActiveTab("overview")}
          >
            Overview
          </button>
          {itinerary.length > 0 && (
            <button
              className={`tgh-showcase-tab ${activeTab === "itinerary" ? "tgh-showcase-tab--active" : ""}`}
              onClick={() => setActiveTab("itinerary")}
            >
              Itinerary ({itinerary.length} Days)
            </button>
          )}
          {(inclusions.length > 0 || exclusions.length > 0) && (
            <button
              className={`tgh-showcase-tab ${activeTab === "inclusions" ? "tgh-showcase-tab--active" : ""}`}
              onClick={() => setActiveTab("inclusions")}
            >
              Inclusions
            </button>
          )}
        </div>

        {/* Tab Content */}
        <div className="tgh-showcase-tab-content">
          {/* Overview */}
          {activeTab === "overview" && (
            <div className="tgh-showcase-overview">
              {description ? (
                <p className="tgh-showcase-desc" style={{ whiteSpace: "pre-line" }}>{description}</p>
              ) : (
                <p className="tgh-showcase-desc">
                  Experience the breathtaking beauty of {location} with our carefully curated itinerary
                  and verified local mountain hosts.
                </p>
              )}

              {highlights.length > 0 && (
                <div className="tgh-showcase-highlights-box">
                  <h4 className="tgh-showcase-subheading">✨ Key Highlights</h4>
                  <ul className="tgh-showcase-list">
                    {highlights.map((h, i) => (
                      <li key={i} className="tgh-showcase-list-item">
                        <span className="tgh-showcase-bullet">✓</span> {toText(h)}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Itinerary */}
          {activeTab === "itinerary" && (
            <div className="tgh-showcase-itinerary">
              {itinerary.map((day, idx) => (
                <div key={idx} className="tgh-showcase-day-card">
                  <div className="tgh-showcase-day-marker">Day {day.day || idx + 1}</div>
                  <div className="tgh-showcase-day-content">
                    <h4 className="tgh-showcase-day-title">{toText(day.title)}</h4>
                    <p className="tgh-showcase-day-details" style={{ whiteSpace: "pre-line" }}>{toText(day.details)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Inclusions & Exclusions */}
          {activeTab === "inclusions" && (
            <div className="tgh-showcase-inclusions-grid">
              {inclusions.length > 0 && (
                <div className="tgh-showcase-inc-box">
                  <h4 className="tgh-showcase-inc-title tgh-showcase-inc-title--yes">Included Amenities</h4>
                  <ul className="tgh-showcase-inc-list">
                    {inclusions.map((item, idx) => (
                      <li key={idx} className="tgh-showcase-inc-item">
                        <span className="tgh-showcase-check">✓</span> {toText(item)}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {exclusions.length > 0 && (
                <div className="tgh-showcase-inc-box">
                  <h4 className="tgh-showcase-inc-title tgh-showcase-inc-title--no">Not Included</h4>
                  <ul className="tgh-showcase-inc-list">
                    {exclusions.map((item, idx) => (
                      <li key={idx} className="tgh-showcase-inc-item">
                        <span className="tgh-showcase-cross">✕</span> {toText(item)}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}